import type {
  CamelCased,
  CampaignChartPoint,
  CampaignMetricTotals,
  CampaignStats,
  ChartResponse,
  MetricServer,
} from '@metapic-apps/shared'

import { normalizeMoney } from '@/lib/adapters/common.adapter'
import type { CampaignStatsPayloadType } from '@/lib/types/campaign-stats.types'
import type { MoneyServerType } from '@/lib/types/common.types'

const CURRENT_SERIES_KEY = 'current'

const toNumericValue = (value: number | MoneyServerType): number =>
  typeof value === 'number' ? value : normalizeMoney(value).amount

export const normalizeCampaignChart = (response: ChartResponse): CampaignChartPoint[] => {
  const series = response.series ?? []
  const current = series.find((item) => item.key === CURRENT_SERIES_KEY) ?? series[0]

  return (current?.data ?? []).map((point) => ({
    date: point.date,
    value: toNumericValue(point.value),
    breakdown: (point.breakdown ?? []).map((entry) => ({
      key: entry.key,
      value: toNumericValue(entry.value),
    })),
  }))
}

export const normalizeCampaignMetricTotals = (
  metrics: CamelCased<MetricServer>[],
): CampaignMetricTotals =>
  metrics.reduce<CampaignMetricTotals>((acc, metric) => {
    acc[metric.key] = toNumericValue(metric.value)
    return acc
  }, {})

export const normalizeCampaignStats = (stats: CampaignStatsPayloadType): CampaignStats => ({
  summary: {
    linksCreated: stats.summary.links_created,
    clicks: stats.summary.clicks,
    orders: stats.summary.orders,
    orderValue: stats.summary.order_value / 100,
    estimatedCost: stats.summary.estimated_cost / 100,
    estimatedEarnings: stats.summary.estimated_earning / 100,
    estimatedGp: stats.summary.estimated_gp / 100,
    estimatedGpRatio: stats.summary.estimated_earning_to_cost_ratio,
  },
  dailyStats: stats.daily_stats.map((dayStats) => ({
    clicks: dayStats.clicks,
    estimatedCost: dayStats.estimated_cost / 100,
    estimatedEarnings: dayStats.estimated_earning / 100,
    estimatedGp: dayStats.estimated_gp / 100,
    estimatedGpRatio: dayStats.estimated_earning_to_cost_ratio,
    linksCreated: dayStats.links_created,
    orderValue: dayStats.order_value / 100,
    orders: dayStats.orders,
    startDate: dayStats.period_start_date,
    sources: dayStats.sources.map((source) => ({
      clicks: source.clicks,
      estimatedCost: source.estimated_cost / 100,
      estimatedEarnings: source.estimated_earning / 100,
      estimatedGp: source.estimated_gp / 100,
      estimatedGpRatio: source.estimated_earning_to_cost_ratio,
      orderValue: source.order_value / 100,
      orders: source.orders,
      source: source.source,
    })),
  })),
})
